import React, { useEffect, useState } from 'react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { Collapse, Grid, IconButton } from '@material-ui/core';
import { SdmTypography, SdmSpacer } from 'components';
import { intlFormat } from 'date-fns';
import { useDataHelper } from 'utils/dataHelper';
import { slotStatus } from 'utils/constants';
import Request from './Request';

const RequestCard = ({
  appointment,
  alreadyOpen = false,
  isSmDown,
  isXsDown,
  i18n,
  locale,
  onViewRequest,
  onDeleteRequest,
  onCheckRequest,
  searchText,
}) => {
  // Hooks
  const dataHelper = useDataHelper();
  // States
  const [collapsableCtrl, setCollapsableCtrl] = useState(false);
  const [slots, setSlots] = useState([]);
  const [originalSlots, setOriginalSlots] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [confirmedRequests, setConfirmedRequests] = useState([]);
  const [checkedInRequests, setCheckedInRequests] = useState([]);
  const [hide, setHide] = useState(false);
  // Effects
  useEffect(() => {
    if (alreadyOpen) setCollapsableCtrl(true);
  }, [alreadyOpen]);

  useEffect(() => {
    if (appointment && appointment.slots?.items?.length) {
      setSlots(
        appointment.slots?.items.filter((s) => s.occupants?.items?.length)
      );
      setOriginalSlots(
        appointment.slots?.items.filter((s) => s.occupants?.items?.length)
      );
    }
  }, [appointment]);

  useEffect(() => {
    if (searchText && searchText.length >= 3) {
      const filteredSlots = slots.map((s) => {
        const str = searchText.replace(' ', '').toLowerCase();

        const filteredOccupants = s.occupants.items.filter((o) =>
          `${o.givenName}${o.familyName}`.toLowerCase().includes(str)
        );
        return {
          ...s,
          occupants: { items: filteredOccupants || [] },
        };
      });

      const newSlots = filteredSlots.filter((s) => s.occupants?.items?.length);
      setHide(!newSlots.length);
      setSlots(newSlots);
      setCollapsableCtrl(true);
    } else if (originalSlots.length) {
      setCollapsableCtrl(true);
      setHide(true);
      setSlots(originalSlots);
    }
  }, [searchText]);

  useEffect(() => {
    if (slots?.length || (searchText && searchText.length)) {
      const _pending = [];
      const _confirmed = [];
      const _checked = [];
      slots.map((s) => {
        const effectiveSlotCapacity =
          parseInt(s.slotCapacity) -
          s.occupants.items.filter((i) => i.status !== slotStatus.PENDING)
            .length;
        s.occupants?.items?.map((o) => {
          const input = {
            ...o,
            bookingReqId: o.id,
            start: s.start,
            end: s.end,
            availableSlots: effectiveSlotCapacity,
          };
          switch (o.status) {
            case slotStatus.PENDING:
              _pending.push(input);
              break;
            case slotStatus.CONFIRMED:
              _confirmed.push(input);
              break;
            case slotStatus.CHECKED:
              _checked.push(input);
              break;
            default:
              break;
          }
        });
      });
      setPendingRequests(_pending);
      setConfirmedRequests(_confirmed);
      setCheckedInRequests(_checked);
    }
  }, [slots]);

  // Functions
  const handleExpandClick = () => {
    setCollapsableCtrl(!collapsableCtrl);
  };

  const handleView = (request) => onViewRequest && onViewRequest(request);
  const handleDelete = (request) => onDeleteRequest && onDeleteRequest(request);
  const handleCheckIn = (request) => onCheckRequest && onCheckRequest(request);

  // Constants
  const date = intlFormat(
    new Date(appointment?.date),
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    },
    {
      locale: dataHelper.getLangCode(locale),
    }
  );

  if (hide) return null;
  return (
    <>
      <div
        style={{
          borderBottom: '1px solid #979797',
          width: '100%',
          paddingBottom: 10,
          marginTop: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <SdmTypography margin={0} variant={isSmDown ? 'h5' : 'h1'}>
          {date}
        </SdmTypography>
        <IconButton
          disableFocusRipple
          disableTouchRipple
          disableRipple
          onClick={() => handleExpandClick()}
        >
          {collapsableCtrl ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </div>
      <SdmSpacer height={20} />
      <Grid container>
        <Collapse in={collapsableCtrl} style={{ width: '100%' }}>
          {pendingRequests.length ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <SdmTypography
                  margin={0}
                  variant={isSmDown ? 'h5Regular' : 'h1Regular'}
                  style={{ textTransform: 'uppercase' }}
                >
                  {i18n.request} ({pendingRequests.length})
                </SdmTypography>
              </div>
              <SdmSpacer height={20} />
              {pendingRequests.map((r, idx) => (
                <Request
                  key={idx}
                  request={r}
                  isSmDown={isSmDown}
                  isXsDown={isXsDown}
                  i18n={i18n}
                  context={slotStatus.PENDING}
                  onView={handleView}
                />
              ))}
              <SdmSpacer height={isSmDown ? 20 : 40} />
            </>
          ) : null}
          {confirmedRequests.length ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <SdmTypography
                  margin={0}
                  variant={isSmDown ? 'h5Regular' : 'h1Regular'}
                  style={{ textTransform: 'uppercase' }}
                >
                  {i18n.confirmed} ({confirmedRequests.length})
                </SdmTypography>
              </div>
              <SdmSpacer height={20} />
              {confirmedRequests.map((r, idx) => (
                <Request
                  key={idx}
                  request={r}
                  isSmDown={isSmDown}
                  isXsDown={isXsDown}
                  i18n={i18n}
                  context={slotStatus.CONFIRMED}
                  onView={handleView}
                  onDelete={handleDelete}
                  onCheckIn={handleCheckIn}
                />
              ))}
              <SdmSpacer height={isSmDown ? 20 : 40} />
            </>
          ) : null}
          {checkedInRequests.length ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <SdmTypography
                  margin={0}
                  variant={isSmDown ? 'h5Regular' : 'h1Regular'}
                  style={{ textTransform: 'uppercase' }}
                >
                  {i18n.checkedIn} ({checkedInRequests.length})
                </SdmTypography>
              </div>
              <SdmSpacer height={20} />
              {checkedInRequests.map((r, idx) => (
                <Request
                  key={idx}
                  request={r}
                  isSmDown={isSmDown}
                  isXsDown={isXsDown}
                  i18n={i18n}
                  context={slotStatus.CHECKED}
                  onView={handleView}
                />
              ))}
              <SdmSpacer height={isSmDown ? 20 : 40} />
            </>
          ) : null}
        </Collapse>
        <SdmSpacer height={40} />
      </Grid>
    </>
  );
};
export default RequestCard;
